import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useI18n, type TFn } from "../i18n";
import { buildMenu, type NavNode } from "../nav/menu";
import { href, type Route } from "../router/hash";
import { useSession, type TenantData } from "../state/session";
import { Dialog } from "./Dialog";
import { Icon, type IconName } from "./Icon";

export type QuickItem = { id: string; kind: "screen" | "site" | "vehicle" | "device" | "vehicleList"; label: string; detail?: string; href: string; icon: IconName };

const PER_KIND = 6;
const MAX = 14;

/**
 * Everything the quick switcher can jump to, built only from what the console has already loaded: the
 * role-filtered menu (so a screen the role may not open is never offered), the visible sites, vehicles and
 * devices of the tenant data (RLS already limited those). No extra query.
 */
export function quickItems(data: TenantData | null, menu: NavNode[], t: TFn): QuickItem[] {
  const out: QuickItem[] = [];
  const walk = (nodes: NavNode[], group?: string) => {
    for (const n of nodes) {
      if (n.kind === "group") walk(n.children ?? [], n.labelKey ? t(n.labelKey) : undefined);
      else if (n.kind === "item" && n.href && n.labelKey) {
        out.push({ id: `screen:${n.id}`, kind: "screen", label: t(n.labelKey), detail: group, href: n.href, icon: n.icon ?? "chevronRight" });
        // Sites hang under the "Sites" item; vehicles come from the data below (the menu only has a site's own).
        if (n.children) walk(n.children.filter((c) => c.kind !== "vehicle"), group);
      } else if (n.kind === "site" && n.href) {
        out.push({ id: n.id, kind: "site", label: n.label ?? "", detail: t("nav.sites"), href: n.href, icon: "building" });
        walk((n.children ?? []).filter((c) => c.kind === "site"), group);
      }
    }
  };
  walk(menu);
  if (data) {
    const siteName = new Map(data.sites.map((s) => [s.id, s.name]));
    for (const v of data.vehicles) {
      const detail = [v.make, v.model, siteName.get(v.siteId)].filter(Boolean).join(" · ");
      out.push({ id: `vehicle:${v.id}`, kind: "vehicle", label: v.fleetNo, detail: [detail, v.serialNo].filter(Boolean).join(" · "), href: `#/vehicles/${encodeURIComponent(v.id)}`, icon: "truck" });
    }
    const fleetNo = new Map(data.vehicles.map((v) => [v.id, v.fleetNo]));
    for (const d of data.devices) {
      const on = d.vehicleId ? fleetNo.get(d.vehicleId) : undefined;
      // Devices have no page of their own: open the vehicle's device tab, or the devices list.
      out.push({
        id: `device:${d.deviceId}`,
        kind: "device",
        label: d.deviceId,
        detail: on ? t("quick.deviceOn", { vehicle: on }) : t("device.unpaired"),
        href: d.vehicleId ? `#/vehicles/${encodeURIComponent(d.vehicleId)}/device` : "#/devices",
        icon: "chip",
      });
    }
  }
  return out;
}

/** Case- and accent-insensitive match on label and detail; label matches and prefix matches rank first. */
export function searchQuick(items: QuickItem[], query: string, locale: string): QuickItem[] {
  const fold = (s: string) => s.toLocaleLowerCase(locale).normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ı/g, "i");
  const q = fold(query.trim());
  if (!q) return items.filter((i) => i.kind === "screen").slice(0, MAX);
  const scored: Array<{ item: QuickItem; score: number }> = [];
  for (const item of items) {
    const label = fold(item.label);
    const detail = fold(item.detail ?? "");
    const score = label.startsWith(q) ? 0 : label.includes(q) ? 1 : detail.includes(q) ? 2 : -1;
    if (score >= 0) scored.push({ item, score });
  }
  scored.sort((a, b) => a.score - b.score);
  const perKind = new Map<string, number>();
  const out: QuickItem[] = [];
  for (const { item } of scored) {
    const n = perKind.get(item.kind) ?? 0;
    if (n >= PER_KIND) continue;
    perKind.set(item.kind, n + 1);
    out.push(item);
    if (out.length >= MAX) break;
  }
  return out;
}

/**
 * "Show all matching vehicles": the quick switcher lists at most PER_KIND vehicles, so a query that matches
 * vehicles also offers the full vehicles list for it (the top bar's vehicle search, which folds into the quick
 * switcher on touch tablets). On the vehicles list the current site and type filters are kept.
 */
export function vehicleListItem(items: QuickItem[], query: string, locale: string, route: Route | undefined, t: TFn): QuickItem | null {
  const q = query.trim();
  if (!q) return null;
  if (searchQuick(items.filter((i) => i.kind === "vehicle"), q, locale).length === 0) return null;
  const onList = route?.name === "vehicles";
  const site = onList ? (route.query.get("site") ?? undefined) : undefined;
  const type = onList ? (route.query.get("type") ?? undefined) : undefined;
  return {
    id: "vehicles:all",
    kind: "vehicleList",
    label: t("quick.allVehicles"),
    detail: t("quick.allVehiclesDetail", { query: q }),
    href: href("/vehicles", { q, site, type }),
    icon: "search",
  };
}

/** Ctrl/⌘ + K anywhere in the console (also while typing elsewhere: the shortcut is not a character). */
export function isQuickSearchShortcut(e: { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean {
  return (e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "k";
}

/**
 * Quick switcher (combobox + listbox in a modal dialog): type, move with the arrow keys, Enter opens,
 * Escape closes and focus returns to where it was.
 */
export function QuickSearch({ onClose, onNavigate, route }: { onClose: () => void; onNavigate: (href: string) => void; route?: Route }) {
  const { t, locale } = useI18n();
  const { data, role, platformAdmin } = useSession();
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const menu = useMemo(() => (role ? buildMenu(data?.siteTree ?? [], role, platformAdmin) : []), [data, role, platformAdmin]);
  const all = useMemo(() => quickItems(data, menu, t), [data, menu, t]);
  const matches = useMemo(() => searchQuick(all, q, locale), [all, q, locale]);
  const results = useMemo(() => {
    const list = vehicleListItem(all, q, locale, route, t);
    return list ? [...matches, list] : matches;
  }, [all, matches, q, locale, route, t]);
  const active = results[Math.min(index, results.length - 1)];

  useEffect(() => setIndex(0), [q]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView?.({ block: "nearest" });
  }, [index]);

  const go = (item: QuickItem | undefined) => {
    if (!item) return;
    onClose();
    onNavigate(item.href);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") setIndex((i) => Math.min(i + 1, results.length - 1));
    else if (e.key === "ArrowUp") setIndex((i) => Math.max(i - 1, 0));
    else if (e.key === "Home") setIndex(0);
    else if (e.key === "End") setIndex(results.length - 1);
    else if (e.key === "Enter") go(active);
    else return;
    e.preventDefault();
  };

  const optionId = (i: QuickItem) => `${listId}-${i.id.replace(/[^\w-]/g, "_")}`;
  return (
    <Dialog title={t("quick.title")} description={t("quick.description")} onClose={onClose}>
      <div className="quick-search">
        <label htmlFor="quick-search-input" className="sr-only">
          {t("quick.label")}
        </label>
        <span className="search quick-search-field">
          <Icon name="search" size={16} className="search-icon" />
          <input
            id="quick-search-input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={active ? optionId(active) : undefined}
            autoComplete="off"
            spellCheck={false}
            placeholder={t("quick.placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            data-autofocus
          />
        </span>
        <ul id={listId} ref={listRef} role="listbox" aria-label={t("quick.results")} className="quick-results">
          {results.map((item) => (
            <li
              key={item.id}
              id={optionId(item)}
              role="option"
              aria-selected={item === active}
              className={`quick-result${item === active ? " is-active" : ""}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setIndex(results.indexOf(item))}
              onClick={() => go(item)}
            >
              <Icon name={item.icon} size={16} className="quick-result-icon" />
              <span className={item.kind === "vehicle" || item.kind === "device" ? "quick-result-label mono" : "quick-result-label"}>{item.label}</span>
              {item.detail && <span className="quick-result-detail">{item.detail}</span>}
              <span className="quick-result-kind">{t(`quick.kind.${item.kind}`)}</span>
            </li>
          ))}
        </ul>
        {results.length === 0 && (
          <p className="quick-empty" role="status">
            {t("quick.none", { query: q.trim() })}
          </p>
        )}
        <p className="quick-hint">{t("quick.hint")}</p>
      </div>
    </Dialog>
  );
}
